import { useState, useEffect } from 'react';

interface SplashScreenProps {
  /** Whether the app is ready (config loaded) */
  isReady: boolean;
  /** Callback when fade-out animation completes */
  onFinished: () => void;
  /** Maximum time to show splash (ms) - safety timeout */
  maxDuration?: number;
  /**
   * Where to render the overlay.
   * - fullscreen: covers the whole viewport (default)
   * - container: covers only the nearest positioned ancestor
   */
  scope?: 'fullscreen' | 'container';
}

/**
 * Premium Casino-style Splash Screen
 * Shown while VFX config is loading to prevent theme flash
 */
export default function SplashScreen({
  isReady,
  onFinished,
  maxDuration = 8000,
  scope = 'fullscreen',
}: SplashScreenProps) {
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  // Safety timeout: force fade-out if config takes too long
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isFadingOut) {
        setIsFadingOut(true);
      }
    }, maxDuration);

    return () => clearTimeout(timer);
  }, [maxDuration, isFadingOut]);

  // Start fade-out when ready
  useEffect(() => {
    if (isReady && !isFadingOut) {
      // Small delay to ensure theme is applied before fade starts
      const timer = setTimeout(() => {
        setIsFadingOut(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isReady, isFadingOut]);

  // Remove from DOM after fade-out animation completes
  useEffect(() => {
    if (isFadingOut) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        onFinished();
      }, 600); // match CSS transition duration
      return () => clearTimeout(timer);
    }
  }, [isFadingOut, onFinished]);

  if (!isVisible) return null;

  return (
    <div
      className={`splash-screen ${scope === 'container' ? 'splash-screen--container' : ''} ${isFadingOut ? 'splash-fade-out' : ''}`}
      aria-label="Loading Kiosk Gaming"
    >
      {/* Animated background shimmer */}
      <div className="splash-bg-shimmer" />

      {/* Floating sparkle particles */}
      <div className="splash-particles">
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="splash-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 4}s`,
              animationDuration: `${2 + Math.random() * 3}s`,
              width: `${2 + Math.random() * 4}px`,
              height: `${2 + Math.random() * 4}px`,
            }}
          />
        ))}
      </div>

      {/* Center content */}
      <div className="splash-content">
        {/* Logo glow ring */}
        <div className="splash-logo-ring">
          <div className="splash-logo-inner">
            <span className="splash-logo-k">K</span>
          </div>
        </div>

        {/* App name */}
        <h1 className="splash-title">
          <span className="splash-title-kiosk">KIOSK</span>
          <span className="splash-title-gaming">GAMING</span>
        </h1>

        {/* Loading dots */}
        <div className="splash-loading-dots">
          <div className="splash-dot" style={{ animationDelay: '0s' }} />
          <div className="splash-dot" style={{ animationDelay: '0.2s' }} />
          <div className="splash-dot" style={{ animationDelay: '0.4s' }} />
        </div>
      </div>

      {/* Inline styles - loaded immediately, no external deps */}
      <style>{`
        .splash-screen {
          position: fixed;
          inset: 0;
          z-index: 99999;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #030712;
          opacity: 1;
          transition: opacity 0.6s ease-out;
          overflow: hidden;
        }

        .splash-screen--container {
          position: absolute;
          inset: 0;
          z-index: 50;
        }

        .splash-fade-out {
          opacity: 0;
          pointer-events: none;
        }

        /* Animated background shimmer */
        .splash-bg-shimmer {
          position: absolute;
          inset: -50%;
          background: radial-gradient(
            ellipse at 50% 50%,
            rgba(139, 92, 246, 0.1) 0%,
            rgba(99, 60, 224, 0.05) 30%,
            transparent 70%
          );
          animation: splash-shimmer 4s ease-in-out infinite;
        }

        @keyframes splash-shimmer {
          0%, 100% {
            transform: scale(1) rotate(0deg);
            opacity: 0.6;
          }
          50% {
            transform: scale(1.2) rotate(3deg);
            opacity: 1;
          }
        }

        /* Floating particles */
        .splash-particles {
          position: absolute;
          inset: 0;
          overflow: hidden;
        }

        .splash-particle {
          position: absolute;
          background: radial-gradient(circle, rgba(139, 92, 246, 0.8), transparent);
          border-radius: 50%;
          animation: splash-float linear infinite;
          opacity: 0;
        }

        @keyframes splash-float {
          0% {
            opacity: 0;
            transform: translateY(0) scale(0);
          }
          20% {
            opacity: 0.8;
            transform: translateY(-20px) scale(1);
          }
          80% {
            opacity: 0.4;
          }
          100% {
            opacity: 0;
            transform: translateY(-120px) scale(0.3);
          }
        }

        /* Center content */
        .splash-content {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
        }

        /* Logo glow ring */
        .splash-logo-ring {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          background: conic-gradient(
            from 0deg,
            rgba(139, 92, 246, 0.7),
            rgba(99, 60, 224, 0.2),
            rgba(168, 85, 247, 0.7),
            rgba(99, 60, 224, 0.2),
            rgba(139, 92, 246, 0.7)
          );
          display: flex;
          align-items: center;
          justify-content: center;
          animation: splash-ring-rotate 3s linear infinite;
          padding: 3px;
        }

        .splash-logo-inner {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background: #030712;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .splash-logo-k {
          font-family: var(--font-display);
          font-size: var(--type-money-hero);
          font-weight: 900;
          font-style: italic;
          background: linear-gradient(135deg, #8B5CF6, #A855F7, #7C3AED);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: splash-glow 2s ease-in-out infinite;
          filter: drop-shadow(0 0 12px rgba(139, 92, 246, 0.4));
        }

        @keyframes splash-ring-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes splash-glow {
          0%, 100% {
            filter: drop-shadow(0 0 12px rgba(139, 92, 246, 0.4));
          }
          50% {
            filter: drop-shadow(0 0 24px rgba(139, 92, 246, 0.8));
          }
        }

        /* Title */
        .splash-title {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
          margin: 0;
        }

        .splash-title-kiosk {
          font-family: var(--font-display);
          font-size: var(--fs-display);
          font-weight: 900;
          font-style: italic;
          letter-spacing: 0.2em;
          background: linear-gradient(135deg, #8B5CF6, #A855F7, #7C3AED);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          filter: drop-shadow(0 0 8px rgba(139, 92, 246, 0.3));
        }

        .splash-title-gaming {
          font-family: var(--font-ui);
          font-size: var(--fs-base);
          font-weight: 600;
          letter-spacing: 0.5em;
          color: rgba(255, 255, 255, 0.5);
        }

        /* Loading dots */
        .splash-loading-dots {
          display: flex;
          gap: 8px;
          margin-top: 12px;
        }

        .splash-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(139, 92, 246, 0.7);
          animation: splash-dot-bounce 1.2s ease-in-out infinite;
        }

        @keyframes splash-dot-bounce {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.3;
          }
          30% {
            transform: translateY(-10px);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
